import {
  Dashboard,
  MapOutlined,
  PeopleAltOutlined,
  BadgeOutlined,
  ArrowOutward,
  CorporateFareOutlined,
  DesignServicesSharp,
  HelpOutline,
  CreditCard,
  Receipt,
  ReceiptLongOutlined,
  FileCopyOutlined,
} from '@mui/icons-material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import NotificationImportantRoundedIcon from '@mui/icons-material/NotificationImportantRounded';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import React from 'react';

export interface MenuItem {
  name: string;
  // Sprint 28, story-142. Key into the `menu` locale section (docs/ux/i18n-house-style.md).
  // `name` stays the English identifier used for feature-flag/testid/equality checks elsewhere
  // in the sidebar; `translationKey` is what LeftSidebar renders.
  translationKey: string;
  icon: React.ReactElement;
  link?: string;
  feature: string;
  rightSidebar?: boolean;
  isModal?: boolean;
}

const iconSize = { fontSize: 20 };

export const menuItems: MenuItem[] = [
  // Dashboard
  {
    name: 'Dashboard',
    translationKey: 'dashboard',
    icon: <Dashboard sx={iconSize} />,
    link: '/dashboard',
    feature: 'DASHBOARD' as const,
  },
  // Map View
  {
    name: 'Map View',
    translationKey: 'mapView',
    icon: <MapOutlined sx={iconSize} />,
    link: '/mapView',
    feature: 'MAP_VIEW' as const,
  },
  // Clients
  {
    name: 'Clients',
    translationKey: 'clients',
    link: '/clients',
    rightSidebar: true,
    icon: <PeopleAltOutlined sx={iconSize} />,
    feature: 'CLIENTS' as const,
  },
  // Employees/Users
  {
    name: 'Users',
    translationKey: 'users',
    icon: <BadgeOutlined sx={iconSize} />,
    link: '/users',
    feature: 'USERS' as const,
    rightSidebar: true,
  },
  // Crews
  {
    name: 'Crews',
    translationKey: 'crews',
    icon: <GroupsOutlinedIcon sx={iconSize} />,
    link: '/crews',
    feature: 'CREWS' as const,
    rightSidebar: true,
  },
  // Projects
  {
    name: 'Projects',
    translationKey: 'projects',
    icon: <WorkOutlineIcon sx={iconSize} />,
    link: '/projects',
    feature: 'PROJECTS' as const,
    rightSidebar: true,
  },
  // Invoices
  {
    name: 'Invoices',
    translationKey: 'invoices',
    icon: <ReceiptLongOutlined sx={iconSize} />,
    link: '/invoices',
    feature: 'INVOICES' as const,
  },
  // Subcontractors
  {
    name: 'Subcontractors',
    translationKey: 'subcontractors',
    icon: <EngineeringOutlinedIcon sx={iconSize} />,
    link: '/subcontractors',
    feature: 'SUBCONTRACTORS' as const,
    rightSidebar: true,
  },
  // Schedule
  {
    name: 'Schedule',
    translationKey: 'schedule',
    icon: <EventNoteOutlinedIcon sx={iconSize} />,
    link: '/schedule',
    feature: 'SCHEDULE' as const,
  },
  // Bids
  {
    name: 'Bids',
    translationKey: 'bids',
    icon: <ArrowOutward sx={iconSize} />,
    link: '/budgets',
    feature: 'BUDGETS' as const,
  },
  // Documents
  {
    name: 'Documents',
    translationKey: 'documents',
    icon: <FolderOpenIcon sx={iconSize} />,
    link: '/documents',
    feature: 'DOCUMENTS' as const,
  },
  // Templates
  {
    name: 'Templates',
    translationKey: 'templates',
    icon: <FileCopyOutlined sx={iconSize} />,
    link: '/templates',
    feature: 'TEMPLATES' as const,
  },
  // Incidents
  {
    name: 'Incidents',
    translationKey: 'incidents',
    icon: <NotificationImportantRoundedIcon sx={iconSize} />,
    link: '/incidents',
    feature: 'INCIDENTS' as const,
  },
  // Warehouse
  {
    name: 'Warehouse',
    translationKey: 'warehouse',
    icon: <WarehouseOutlinedIcon sx={iconSize} />,
    link: '/warehouse',
    feature: 'WAREHOUSE' as const,
    rightSidebar: true,
  },
  // Geofences
  {
    name: 'Geofences',
    translationKey: 'geofences',
    icon: <PinDropOutlinedIcon sx={iconSize} />,
    link: '/geofences',
    feature: 'GEOFENCES' as const,
  },
  // Billing (Owner only)
  {
    name: 'Billing',
    translationKey: 'billing',
    icon: <CreditCard sx={iconSize} />,
    link: '/billing',
    feature: 'BILLING' as const,
  },
  // Organizations (Super Admin only)
  {
    name: 'Organizations',
    translationKey: 'organizations',
    icon: <CorporateFareOutlined sx={iconSize} />,
    link: '/organizations',
    feature: 'ORGANIZATIONS' as const,
  },
  {
    name: 'Subscription Plans',
    translationKey: 'subscriptionPlans',
    icon: <Receipt sx={iconSize} />,
    link: '/subscription-plans',
    feature: 'SUBSCRIPTION_PLANS' as const,
  },
  {
    name: 'User Admin',
    translationKey: 'userAdmin',
    icon: <AccountBoxIcon sx={iconSize} />,
    link: '/user-admin',
    feature: 'ORGANIZATIONS' as const,
  },
  {
    name: 'Warehouse Config',
    translationKey: 'warehouseConfig',
    icon: <CategoryOutlinedIcon sx={iconSize} />,
    link: '/admin/warehouse/categories',
    feature: 'ORGANIZATIONS' as const,
  },
  {
    name: 'Notification Recipients',
    translationKey: 'notificationRecipients',
    icon: <NotificationImportantRoundedIcon sx={iconSize} />,
    link: '/admin/notification-recipients',
    feature: 'SUPER_ADMIN_NOTIFICATIONS' as const,
  },
  {
    name: 'Create Invoice',
    translationKey: 'createInvoice',
    icon: <ReceiptLongOutlined sx={iconSize} />,
    link: '/admin/invoices/new',
    feature: 'STANDALONE_INVOICE' as const,
  },
  {
    name: 'Field Capture',
    translationKey: 'fieldCapture',
    icon: <FactCheckOutlinedIcon sx={iconSize} />,
    link: '/admin/field-capture',
    feature: 'FIELD_CAPTURE_ADMIN' as const,
  },
  {
    name: 'Platform Status',
    translationKey: 'platformStatus',
    icon: <MonitorHeartOutlinedIcon sx={iconSize} />,
    link: '/admin/platform-status',
    feature: 'PLATFORM_STATUS' as const,
  },
  {
    name: 'Settings',
    translationKey: 'settings',
    icon: <SettingsIcon sx={iconSize} />,
    link: '/settings',
    feature: 'ORGANIZATIONS' as const,
  },
  {
    name: 'Tutorials',
    translationKey: 'tutorials',
    icon: <HelpOutline sx={iconSize} />,
    feature: 'TUTORIALS' as const,
    isModal: true,
  },
  // Sprint 32, story-165 / Sprint 37, story-196. Links to the Assistant page (always org-wide and
  // empty — project context comes only from a project's own button). Hidden entirely while `assistantAvailable` is false (LeftSidebar's own gate,
  // since that check needs a live query, not just the static role-based FEATURE_PERMISSIONS map).
  {
    name: 'Assistant',
    translationKey: 'assistant',
    icon: <SmartToyOutlinedIcon sx={iconSize} />,
    link: '/assistant',
    feature: 'ASSISTANT' as const,
  },
  // Sprint 33, story-170. Hidden entirely while `dailySiteReportAvailable` is false
  // (LeftSidebar's own gate, same shape as Assistant above).
  {
    name: 'Daily reports',
    translationKey: 'dailyReports',
    icon: <SummarizeOutlinedIcon sx={iconSize} />,
    link: '/reports/daily',
    feature: 'DAILY_SITE_REPORTS' as const,
  },
  {
    name: 'UI Components',
    translationKey: 'uiComponents',
    icon: <DesignServicesSharp sx={iconSize} />,
    link: '/ui',
    feature: 'COMPONENTS' as const,
  },
];
